import { initializeApp } from 'firebase/app';
import {
  getAnalytics,
  logEvent as _firebaseLogEvent,
  setUserId as _firebaseSetUserId,
  setUserProperties as _firebaseSetUserProperties,
} from 'firebase/analytics';
import { getAuth, initializeAuth, indexedDBLocalPersistence, signInAnonymously, getRedirectResult } from 'firebase/auth';
import { initializeFirestore, getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { isNativeApp } from './services/platform-service.js';

const firebaseConfig = {
  apiKey: 'AIzaSyBNUSEen7OLxnlIisEJBO4tj5aidfyKpXs',
  // archelier.co 으로 OAuth handler 라우팅 — Apple Service ID 의 Return URLs
  // 에 등록한 도메인과 일치시키기 위함. 여기를 firebaseapp.com 으로 두면
  // Apple OAuth 가 voda-7647c.firebaseapp.com/__/auth/handler 으로 redirect
  // 시도하는데 Apple 이 그 도메인을 사용자 도메인으로 인식 안 해서
  // invalid_request 발생.
  authDomain: 'archelier.co',
  projectId: 'voda-7647c',
  storageBucket: 'voda-7647c.firebasestorage.app',
  messagingSenderId: '762268177686',
  appId: '1:762268177686:web:0bfee1635e591ec318ac37',
  measurementId: 'G-W9MWB4EM8E',
};

const app = initializeApp(firebaseConfig);

// firebase/analytics 의 JS SDK 는 웹 전용 — Capacitor WKWebView 안에서
// 초기화하면 "cookie not allowed" / measurement protocol 오류가 위로 전파돼서
// firebase.js 의 나머지 export 까지 깨진다. 그래서 네이티브에서는 대신
// @capacitor-firebase/analytics 플러그인 (네이티브 Firebase SDK) 을 쓴다.
const NATIVE = isNativeApp();
let _analytics = null;
try {
  if (!NATIVE) _analytics = getAnalytics(app);
} catch (err) {
  console.warn('Firebase analytics init failed:', err);
}
// 네이티브에서는 _analytics 가 null. analytics 를 직접 import 해서
// Firebase 의 logEvent / setUserId 에 넘기면 firebase/analytics 가 내부적으로
// analytics.app.options 같은 필드 접근하다 throw → 앱 렌더 실패.
// 그래서 두 가지 안전장치:
//   1. analytics 를 항상 truthy 객체로 export (call site 가 falsy check 안 해도 OK)
//   2. 아래 wrapper 들이 플랫폼 분기를 흡수 — 호출부는 분기하지 않는다.
export const analytics = _analytics || { __noop: true };

// 플러그인은 Capacitor proxy 라 "모든 프로퍼티 접근" 이 네이티브 메서드 호출로
// 바뀐다. 이게 promise resolution 자리에 들어가면 (async 함수 반환값이나 .then
// 콜백) 런타임이 `.then` 을 프로브하고 브리지가
// "FirebaseAnalytics.then() is not implemented on ios" 를 던진다 — 애널리틱스
// 호출마다 unhandled rejection 폭탄. 평범한 { A } 객체로 감싸면 thenable 프로브가
// proxy 에 닿지 않는다. import 는 캐시.
let _nativeAnalytics;
function nativeAnalytics() {
  if (!_nativeAnalytics) {
    _nativeAnalytics = import('@capacitor-firebase/analytics')
      .then(m => ({ A: m.FirebaseAnalytics }))
      .catch((err) => {
        // 일시적인 chunk load 실패를 memoize 하면 안 된다 — 캐시된 {A:null} 때문에
        // 앱 재시작 전까지 애널리틱스가 죽는다. 캐시를 비워 다음 호출이 재시도.
        _nativeAnalytics = null;
        console.warn('analytics plugin import failed:', err?.message);
        return { A: null };
      });
  }
  return _nativeAnalytics;
}

export function logEvent(_unused, eventName, params) {
  if (NATIVE) {
    nativeAnalytics().then(({ A }) => A && A.logEvent({ name: eventName, params: params || {} })).catch(() => {});
    return;
  }
  if (!_analytics) return;
  try {
    _firebaseLogEvent(_analytics, eventName, params);
  } catch (err) {
    console.warn('logEvent failed:', eventName, err?.message);
  }
}

export function setUserId(_unused, uid) {
  if (NATIVE) {
    nativeAnalytics().then(({ A }) => A && A.setUserId({ userId: uid || null })).catch(() => {});
    return;
  }
  if (!_analytics) return;
  try {
    _firebaseSetUserId(_analytics, uid);
  } catch (err) {
    console.warn('setUserId failed:', err?.message);
  }
}

// User property — 코호트/세그먼트용 (예: plan=free vs pro 리텐션 비교).
export function setUserProp(key, value) {
  if (NATIVE) {
    nativeAnalytics().then(({ A }) => A && A.setUserProperty({ key, value: value == null ? null : String(value) })).catch(() => {});
    return;
  }
  if (!_analytics) return;
  try {
    _firebaseSetUserProperties(_analytics, { [key]: value });
  } catch (err) {
    console.warn('setUserProp failed:', err?.message);
  }
}

// 화면 추적 — Firebase 의 Screens 리포트 + 화면 체류시간(engagement) 지표를
// 채운다. 라우트가 바뀔 때마다 호출.
export function logScreen(screenName) {
  if (NATIVE) {
    nativeAnalytics().then(({ A }) => A && A.setCurrentScreen({ screenName, screenClassOverride: screenName })).catch(() => {});
    return;
  }
  if (!_analytics) return;
  try {
    _firebaseLogEvent(_analytics, 'screen_view', {
      firebase_screen: screenName,
      firebase_screen_class: screenName,
    });
  } catch (err) {
    console.warn('logScreen failed:', err?.message);
  }
}

// Auth — under the default getAuth() init, Firebase dynamically loads its
// popup-based OAuth helper from apis.google.com/_/scs/... whenever any
// redirect/popup-related code path is hit. Inside Capacitor's WKWebView
// with origin capacitor://localhost that cross-origin script load is
// rejected, the resulting throw surfaces as an opaque "Script error.",
// and the main thread dies — Firestore.getDocs() never resolves and the
// feed hangs forever. Use initializeAuth WITHOUT a popupRedirectResolver
// on native, with IndexedDB persistence (the default LOCAL relies on
// localStorage + cookies, which are flaky under custom-scheme origins).
// Apple Sign-In on native goes through the @capacitor-community plugin,
// so we never need the popup helper here.
export const auth = isNativeApp()
  ? initializeAuth(app, { persistence: indexedDBLocalPersistence })
  : getAuth(app);

// Firestore — the default WebChannel transport also struggles inside the
// WKWebView. Force long-polling on native; on the web auto-detect handles
// the same edge cases.
export const db = isNativeApp()
  ? initializeFirestore(app, {
      experimentalForceLongPolling: true,
      useFetchStreams: false,
    })
  : getFirestore(app);

export const storage = getStorage(app);
export const functions = getFunctions(app);

// Auto sign in anonymously if no user session exists. getRedirectResult
// is web-only — calling it on native triggers the popup OAuth helper
// bootstrap that we just took pains to avoid.
const attachAuthState = () => {
  auth.onAuthStateChanged((user) => {
    if (!user) {
      signInAnonymously(auth).catch(err => console.warn('Anonymous auth failed:', err));
    }
  });
};
if (isNativeApp()) {
  attachAuthState();
} else {
  getRedirectResult(auth).catch(() => null).finally(attachAuthState);
}

export default app;
